import React from 'react';
import '../css/recentMediaUpload.css';
import { RetryIcon, SpinnerIcon, TrashRecentIcon } from '../../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

export type RecentMediaUploadStatus = 'uploading' | 'complete' | 'failed';

export interface RecentMediaUploadProps {
  fileName: string;
  uploadedSize: string;
  totalSize: string;
  progress: number;        // 0–100
  status: RecentMediaUploadStatus;
  thumbnailSrc?: string;
  onCancel?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRetry?: () => void;
  className?: string;
}


const CompleteIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <circle cx="4.5" cy="4.5" r="4.5" fill="#84A578" />
    <path d="M2.5 4.6 L3.9 6 L6.5 3" stroke="#FFFFFF" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UncompleteIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <circle cx="4.5" cy="4.5" r="4.5" fill="#FE566B" />
    <path d="M3 3 L6 6 M6 3 L3 6" stroke="#FFFFFF" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

const CancelIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <circle cx="4.5" cy="4.5" r="3.825" stroke="#8A8B8F" strokeWidth="0.8" />
    <path d="M3.2 3.2 L5.8 5.8 M5.8 3.2 L3.2 5.8" stroke="#8A8B8F" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

/* ── Component ──────────────────────────────────────────── */

// status is fully controlled by the parent — no internal state.
// Parent is responsible for:
//   onCancel → set status to 'failed'
//   onRetry  → set status to 'uploading' and restart progress

const RecentMediaUpload: React.FC<RecentMediaUploadProps> = ({
  fileName,
  uploadedSize,
  totalSize,
  progress,
  status,
  thumbnailSrc,
  onCancel,
  onEdit,
  onDelete,
  onRetry,
  className = '',
}) => {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  const statusLabel =
    status === 'uploading' ? 'Uploading' :
      status === 'complete' ? 'Complete' : 'Uncomplete'; //NOSONAR

  return (
    <div className={`scl--cms-components-recentMediaUpload ${className}`.trim()}>

      {/* ── Thumbnail ── */}
      <div className="scl--cms-components-recentMediaUpload-avatar">
        {thumbnailSrc
          ? <img src={thumbnailSrc} alt={fileName} />
          : <div className="scl--cms-components-recentMediaUpload-avatarPlaceholder" />
        }
      </div>

      {/* ── Content ── */}
      <div className="scl--cms-components-recentMediaUpload-content">

        <span className="scl--cms-components-recentMediaUpload-name">{fileName}</span>

        <div className="scl--cms-components-recentMediaUpload-meta">
          <span className="scl--cms-components-recentMediaUpload-size">{uploadedSize} of {totalSize}</span>
          <span className="scl--cms-components-recentMediaUpload-dot">•</span>

          <span className="scl--cms-components-recentMediaUpload-statusIcon">
            {status === 'uploading' && <SpinnerIcon />}
            {status === 'complete' && <CompleteIcon />}
            {status === 'failed' && <UncompleteIcon />}
          </span>

          <span className="scl--cms-components-recentMediaUpload-statusLabel">{statusLabel}</span>

          <span className="scl--cms-components-recentMediaUpload-dot">•</span>

          <div className="scl--cms-components-recentMediaUpload-actions">

            {status === 'uploading' && (
              <button type="button" className="scl--cms-components-recentMediaUpload-btn scl--cms-components-recentMediaUpload-btn--cancel" onClick={onCancel} aria-label="Cancel upload">
                <CancelIcon />
              </button>
            )}

            {status === 'complete' && (
              <button type="button" className="scl--cms-components-recentMediaUpload-btn scl--cms-components-recentMediaUpload-btn--delete" onClick={onDelete} aria-label="Delete">
                <TrashRecentIcon />
              </button>
            )}

            {status === 'failed' && (
              <>
                <button type="button" className="scl--cms-components-recentMediaUpload-btn scl--cms-components-recentMediaUpload-btn--retry" onClick={onRetry} aria-label="Retry">
                  <RetryIcon />
                </button>
                <button type="button" className="scl--cms-components-recentMediaUpload-btn scl--cms-components-recentMediaUpload-btn--delete" onClick={onDelete} aria-label="Delete">
                  <TrashRecentIcon />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="scl--cms-components-recentMediaUpload-track">
          <div
            className={`scl--cms-components-recentMediaUpload-fill scl--cms-components-recentMediaUpload-fill--${status}`}
            style={{ width: `${clampedProgress}%` }}
          />
        </div>

      </div>
    </div>
  );
};

export default RecentMediaUpload;